import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Container, Row } from 'reactstrap';
import {
    Alignment,
    DateInput,
    Icon,
    InputType,
    Item,
    MultiSelectList,
    NumericInput,
    SelectInput,
    TextArea,
    ToogleSwitch,
} from 'tacho-ui';
import config from '../../../config';
import { checkWrongDate, createDateFromTimeZone, dateToStringFormat } from '../../../core/helpers/moment';
import { ApplicationState } from '../../../state/ApplicationState';
import { DispatchType } from '../../../state/configureStore';
import { addLicenceVersionThunk, fetchLicenceThunk } from '../../../state/licences/reducer';
import { TachoModal } from '../../components/TachoModal';
import { TachoEditModalTitle } from '../../components/TachoModal/TachoEditTitle';
import { mapModulesIdsToItems } from '../../pages/modules/services/moduleServices';
import { PaymentStatusEnum } from '../../../core/domainModels/licences/PaymentStatus';
import { getKeyFromEnum } from '../../../core/helpers/enumUtils';
import { AddLicenseVersion, defaultAddLicenseModel } from '../../../core/domainModels/licenceVersions/AddLicenseVersion';
import {
    checkVersionOverlapAdd,
    getDistributorIdForLicenseType,
    isDeveloperLicenseType,
    maxInt,
    modulesChange,
} from './services/licenceService';
import { LicenceErrors } from './GenerateLicenceModal';
import { LicenseTypeEnum } from '../../../core/domainModels/licences/LicenseType';
import useLicenseTypesOptions from '../../hooks/useLicenseTypesOptions';
import { LicenseModel } from '../../../core/domainModels/licences/LicenseModel';

interface AddLicenceVersionModalProps {
    isOpen: boolean;
    licence: LicenseModel;
    onToggle: () => void;
}
export const AddLicenceVersionModal = (props: AddLicenceVersionModalProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch<DispatchType>();
    const modules = useSelector((state: ApplicationState) => state.modules.modules);
    const generateLicencePending = useSelector((state: ApplicationState) => state.licences.generateLicencePending);
    const generateLicenceError = useSelector((state: ApplicationState) => state.licences.generateLicenceError);
    const licenceVersionPending = useSelector((state: ApplicationState) => state.licences.licenceVersionPending);
    const licenceVersionError = useSelector((state: ApplicationState) => state.licences.licenceVersionError);
    const distributors = useSelector((state: ApplicationState) => state.distributors.distributors);
    const [licenceData, setLicenceData] = useState<AddLicenseVersion>({
        ...defaultAddLicenseModel,
        licenseNumber: props.licence.licenseNumber,
    });
    const [errors, setErrors] = useState<LicenceErrors>();

    useEffect(() => {
        if (props.isOpen && props.licence.licenseVersions.length > 0) {
            const lastVersion = props.licence.licenseVersions.reduce((a, b) => (a.validFrom > b.validFrom ? a : b));
            const expectedValidFrom = lastVersion.validTo.plus({ days: 1 }).startOf('day');
            const defaultValidTo = expectedValidFrom.endOf('month');
            setLicenceData({
                ...defaultAddLicenseModel,
                licenseNumber: props.licence.licenseNumber,
                validFrom: expectedValidFrom,
                validTo: defaultValidTo,
                maximumNumberOfSupportedCars: lastVersion.maximumNumberOfSupportedCars,
                noCarsLimit: lastVersion.maximumNumberOfSupportedCars === maxInt,
                allowedModules: lastVersion.allowedModules,
                type: lastVersion.type,
                includeBeta: lastVersion.includeBeta,
                distributorId: getDistributorIdForLicenseType(lastVersion.type, lastVersion.distributorId),
            });
        }
    }, [props.isOpen, props.licence]);

    const validateForm = () => {
        const validated = {
            validFrom: !licenceData.validFrom.isValid,
            validTo: !licenceData.validTo.isValid,
            paymentDeadline: !licenceData.paymentDeadline.isValid,
            maximumNumberOfSupportedCars: licenceData.maximumNumberOfSupportedCars === 0,
            allowedModules:
                licenceData.allowedModules.length === 0 || licenceData.allowedModules.some(item => item.maximumLoggedUsers === 0),
        };
        setErrors(validated);
        return (
            !validated?.allowedModules &&
            !validated?.maximumNumberOfSupportedCars &&
            !validated?.paymentDeadline &&
            !validated?.validFrom &&
            !validated?.validTo
        );
    };

    const onSubmit = () => {
        if (!validateForm()) {
            return;
        }

        dispatch(
            addLicenceVersionThunk({
                ...licenceData,
                licenseNumber: props.licence.licenseNumber,
                maximumNumberOfSupportedCars: licenceData.noCarsLimit ? maxInt : licenceData.maximumNumberOfSupportedCars,
                distributorId: getDistributorIdForLicenseType(licenceData.type, licenceData.distributorId),
            }),
        )
            .then(result => {
                if (result.meta.requestStatus === 'fulfilled') {
                    setLicenceData(defaultAddLicenseModel);
                    props.onToggle();
                    dispatch(fetchLicenceThunk());
                    setErrors({});
                }
            })
            .catch((err: Error) => console.log(err));
    };

    const paymentStatusOptions = useMemo(
        (): Array<Item> => [
            { id: PaymentStatusEnum.Awaiting, label: formatMessage({ id: PaymentStatusEnum.Awaiting }) },
            { id: PaymentStatusEnum.Received, label: formatMessage({ id: PaymentStatusEnum.Received }) },
        ],
        [],
    );

    const licenseTypesOptions = useLicenseTypesOptions();

    const distributorsOptions = useMemo((): Array<Item> => {
        return distributors.map(x => ({
            id: x.id,
            label: x.name,
        }));
    }, [distributors]);

    const checkVersionOverlapMemoized = useMemo(() => {
        return checkVersionOverlapAdd(props.licence, licenceData);
    }, [props.licence, licenceData]);

    const onChangeDateFrom = (value: string) => {
        const change = { ...licenceData };
        if (
            !licenceData.validTo ||
            checkWrongDate(licenceData.validTo) ||
            licenceData.validTo < createDateFromTimeZone(value as string).startOf('day')
        )
            change.validTo = createDateFromTimeZone(value as string).endOf('month');
        setErrors({ ...errors, validFrom: undefined, validTo: undefined });
        return setLicenceData({
            ...change,
            validFrom: createDateFromTimeZone(value as string).startOf('day'),
        });
    };
    const onChangeDateTo = (value: string) => {
        const change = { ...licenceData };
        if (
            !licenceData.validFrom ||
            checkWrongDate(licenceData.validFrom) ||
            licenceData.validFrom > createDateFromTimeZone(value as string).endOf('day')
        )
            change.validFrom = createDateFromTimeZone(value).startOf('month');
        setErrors({ ...errors, validFrom: undefined, validTo: undefined });
        return setLicenceData({
            ...change,
            validTo: createDateFromTimeZone(value as string).endOf('day'),
        });
    };

    return (
        <TachoModal
            size={'md'}
            height={'auto'}
            isOpen={props.isOpen}
            onToggle={() => {
                setLicenceData(defaultAddLicenseModel);
                props.onToggle();
                setErrors({});
            }}
            onSubmit={onSubmit}
            title={<TachoEditModalTitle titleToTranslation="licenceVersionAdd" icon={Icon.Add} />}
            submitLabelToTranslate="commonAdd"
            submitIcon={Icon.Add}
            disableSubmitButton={!!checkVersionOverlapMemoized}
            isPending={generateLicencePending || licenceVersionPending}
            errorToTranslate={generateLicenceError || licenceVersionError}
        >
            <Container fluid className="d-flex flex-column px-4 gap-2">
                <Row>
                    <ToogleSwitch
                        value={licenceData.noCarsLimit || false}
                        label={formatMessage({ id: 'licencesNoCarsLimit' })}
                        onChange={value =>
                            setLicenceData({
                                ...licenceData,
                                noCarsLimit: value,
                            })
                        }
                    />
                </Row>
                <Row>
                    <ToogleSwitch
                        value={licenceData.includeBeta || false}
                        label={formatMessage({ id: 'licencesIncludeBeta' })}
                        onChange={value =>
                            setLicenceData({
                                ...licenceData,
                                includeBeta: value,
                            })
                        }
                    />
                </Row>
                <Row>
                    <NumericInput
                        labelLength={5}
                        inputLength={1}
                        value={licenceData.maximumNumberOfSupportedCars}
                        min={1}
                        max={100}
                        label={formatMessage({ id: 'licencesMaximumNumberOfSupportedCars' })}
                        onChange={value => setLicenceData({ ...licenceData, maximumNumberOfSupportedCars: value })}
                        disabled={licenceData.noCarsLimit || generateLicencePending || licenceVersionPending}
                    />
                </Row>
                <Row>
                    <MultiSelectList
                        hideSelectAll
                        uncontrolled
                        labelLength={-1}
                        rowsToShow={3}
                        label={formatMessage({ id: 'licencesAllowedModules' })}
                        value={mapModulesIdsToItems(licenceData.allowedModules, modules)}
                        placeholder={formatMessage({ id: 'commonSearch' })}
                        selectedLabelsText={formatMessage({ id: 'commonSelected' })}
                        onChange={selectedItems => {
                            setLicenceData(prevState => ({
                                ...licenceData,
                                allowedModules:
                                    modulesChange(selectedItems, prevState)
                                        ?.filter(item => item.checked)
                                        .map(item => ({
                                            tachoModuleId: item.id,
                                            maximumLoggedUsers: (item.additionalValue as number) || 1,
                                        })) || [],
                            }));

                            setErrors({
                                ...errors,
                                allowedModules: selectedItems.filter(item => item.checked).length > 0 ? undefined : true,
                            });
                        }}
                        additionalInput={InputType.Numeric}
                        onAdditionalInputChange={selectedItems => {
                            setLicenceData({
                                ...licenceData,
                                allowedModules: selectedItems
                                    .filter(item => item.checked)
                                    .map(item => ({
                                        tachoModuleId: item.id,
                                        maximumLoggedUsers: (item.additionalValue as number) || 1,
                                    })),
                            });
                        }}
                        disabled={generateLicencePending || licenceVersionPending}
                        errorMessage={errors?.allowedModules ? formatMessage({ id: 'licenceAllowedModulesError' }) : undefined}
                    />
                </Row>
                <Row>
                    <Col>
                        <DateInput
                            gap={5}
                            locale={config.language}
                            value={dateToStringFormat(licenceData.validFrom)}
                            label={formatMessage({ id: 'licencesValidFrom' })}
                            onChange={onChangeDateFrom}
                            disabled={generateLicencePending || licenceVersionPending}
                            errorMessage={
                                (errors?.validFrom && formatMessage({ id: 'licenceEmptyOrWrongDate' })) ||
                                (checkVersionOverlapMemoized && formatMessage({ id: checkVersionOverlapMemoized }))
                            }
                        />
                    </Col>
                    <Col>
                        <DateInput
                            gap={5}
                            locale={config.language}
                            value={dateToStringFormat(licenceData.validTo)}
                            label={formatMessage({ id: 'licencesValidTo' })}
                            onChange={onChangeDateTo}
                            disabled={generateLicencePending || licenceVersionPending}
                            errorMessage={
                                (errors?.validTo && formatMessage({ id: 'licenceEmptyOrWrongDate' })) ||
                                (checkVersionOverlapMemoized && formatMessage({ id: checkVersionOverlapMemoized }))
                            }
                        />
                    </Col>
                </Row>
                <Row>
                    <DateInput
                        locale={config.language}
                        value={dateToStringFormat(licenceData.paymentDeadline)}
                        label={formatMessage({ id: 'licencesPaymentDeadline' })}
                        onChange={value => {
                            setLicenceData({ ...licenceData, paymentDeadline: createDateFromTimeZone(value) });
                            setErrors({ ...errors, paymentDeadline: undefined });
                        }}
                        disabled={generateLicencePending || licenceVersionPending}
                        errorMessage={errors?.paymentDeadline ? formatMessage({ id: 'licenceEmptyOrWrongDate' }) : undefined}
                    />
                </Row>
                <Row>
                    <SelectInput
                        label={formatMessage({ id: 'licencesPaymentStatus' })}
                        value={
                            paymentStatusOptions.find(item => item.id === PaymentStatusEnum[licenceData.paymentStatus])?.label ||
                            ''
                        }
                        options={paymentStatusOptions}
                        onChange={value =>
                            setLicenceData({
                                ...licenceData,
                                paymentStatus: getKeyFromEnum(Object.entries(PaymentStatusEnum), (value as Item).id),
                            })
                        }
                        disabled={generateLicencePending || licenceVersionPending}
                    />
                </Row>
                <Row>
                    <SelectInput
                        label={formatMessage({ id: 'licencesLicenseType' })}
                        value={licenseTypesOptions.find(item => item.id === LicenseTypeEnum[licenceData.type])?.label || ''}
                        options={licenseTypesOptions}
                        onChange={value => {
                            const newType = getKeyFromEnum(Object.entries(LicenseTypeEnum), (value as Item).id);
                            setLicenceData({
                                ...licenceData,
                                type: newType,
                                distributorId: getDistributorIdForLicenseType(newType, licenceData.distributorId),
                            });
                        }}
                        disabled={generateLicencePending || licenceVersionPending}
                    />
                </Row>
                {!isDeveloperLicenseType(licenceData.type) && (
                    <Row>
                        <SelectInput
                            label={formatMessage({ id: 'licencesDistributor' })}
                            value={distributors.find(item => item.id === licenceData.distributorId)?.name || ''}
                            options={distributorsOptions}
                            onChange={value =>
                                setLicenceData({
                                    ...licenceData,
                                    distributorId: (value as Item).id,
                                })
                            }
                        />
                    </Row>
                )}
                <Row>
                    <TextArea
                        align={Alignment.flexStart}
                        value={licenceData.notes}
                        label={formatMessage({ id: 'clientNotes' })}
                        onChange={value => setLicenceData({ ...licenceData, notes: value as string })}
                        labelLength={-1}
                        gap={10}
                    />
                </Row>
            </Container>
        </TachoModal>
    );
};
